import { ButtonHTMLAttributes, CSSProperties, ElementType } from "react";
import Spinner from "../Spinner/Spinner";
import { cn } from "@/shared/utils/cn";
import Icon, { IconsVariant } from "../Icon/Icon";

export type ButtonSize = "default" | "small" | "medium" | "large" | "icon";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  isPending?: boolean;
  isDisabled?: boolean;
  variant?: "default" | "danger" | "secondary" | "tertiary" | "icon";
  size?: ButtonSize;
  isActive?: boolean;
  borderRadius?: "default" | "small" | "medium" | "large" | "icon";
  bg?: string;
  as?: ElementType;
  icon?: IconsVariant;
}

const sizeClasses: Record<ButtonSize, string> = {
  default: "px-btn-default-x py-btn-default-y text-default leading-8",
  small: "px-btn-small-x py-btn-small-y text-btn-small leading-7",
  medium: "px-btn-medium-x py-btn-medium-y text-default leading-8",
  large: "w-full px-btn-large-x py-btn-large-y text-btn-large leading-8",
  icon: "",
};

const radiusClasses: Record<ButtonSize, string> = {
  default: "rounded-btn-default",
  small: "rounded-btn-small",
  medium: "rounded-btn-medium",
  large: "rounded-btn-large",
  icon: "rounded-btn-icon"
};

const variantClasses: Record<NonNullable<ButtonProps["variant"]>, string> = {
  default:
    "border-none outline-none bg-primary text-white hover:bg-[color-mix(in_srgb,var(--color-primary)_90%,white)] focus:shadow-btn-focus-primary active:shadow-btn-focus-primary",
  danger:
    "border-none outline-none bg-error text-white hover:opacity-90 focus:shadow-btn-focus-danger active:shadow-btn-focus-danger",
  secondary:
    "bg-white text-primary border-2 border-primary hover:text-white hover:bg-[color-mix(in_srgb,var(--color-primary)_90%,white)] focus:text-dark focus:bg-[color-mix(in_srgb,var(--color-primary)_90%,white)] active:text-dark active:bg-[color-mix(in_srgb,var(--color-primary)_90%,white)] focus:shadow-btn-focus-secondary active:shadow-btn-focus-secondary",
  tertiary: "border-none outline-none w-full rounded-none text-btn-tertiary",
  icon: "group inline-flex min-h-[3.8rem] shrink-0 cursor-pointer rounded-lg items-center justify-center gap-2 whitespace-nowrap border-none bg-white px-4 py-[0.85rem] text-default font-medium text-text-tertiary outline-none transition-[color,box-shadow] duration-200 hover:text-primary md:w-auto md:justify-start",

};

const Button = ({
  isPending = false,
  isDisabled = false,
  variant = "default",
  size = "default",
  isActive = false,
  borderRadius,
  bg,
  as: Component = "button",
  className = "",
  children,
  style,
  icon,
  ...rest
}: ButtonProps) => {
  const isIconVariant = variant === "icon";
  const isButtonDisabled = isDisabled || isPending;
  const mergedStyle: CSSProperties = {
    ...(borderRadius ? { borderRadius } : {}),
    ...(bg ? { background: bg } : {}),
    ...(style || {}),
  };

  return (
    <Component
      {...rest}
      disabled={isButtonDisabled}
      className={cn(
        isIconVariant
          ? ["font-medium transition-all duration-200", variantClasses.icon]
          : [
              "font-semibold transition-all duration-200",
              borderRadius ? "" : radiusClasses[size],
              variantClasses[variant],
              sizeClasses[size],
            ],
        isDisabled
          ? "bg-disabled text-[#7E7E7E] hover:bg-disabled hover:text-[#7E7E7E] focus:bg-disabled focus:text-[#7E7E7E] active:bg-disabled active:text-[#7E7E7E] focus:shadow-btn-focus-disabled active:shadow-btn-focus-disabled"
          : "",
        isActive ? "bg-primary-dark text-white" : "",
        isPending ? "opacity-40 pointer-events-none select-none" : "",
        isButtonDisabled ? "cursor-not-allowed" : "cursor-pointer",
        className
      )}
      style={mergedStyle}
    >
      {isPending ? <Spinner color="white" size="0.2rem" /> : isIconVariant && icon ? (
        <>
          <Icon
            className="h-5 w-5 shrink-0 fill-primary/80 transition-colors duration-200 group-hover:fill-primary"
            variants={icon}
          />
          <span className="min-w-0 truncate">{children}</span>
        </>
      ) : (
        children
      )}
    </Component>
  );
};

export default Button;
